import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { KnowledgeNoteInput } from "@gb/contracts";
import { api, errorMessage } from "../../api/client";

export const brainKeys = {
  items: (tenantId: string, projectId: string) =>
    ["tenants", tenantId, "projects", projectId, "brain"] as const,
};

export function useBrainItems(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: brainKeys.items(tenantId, projectId),
    queryFn: async () => {
      const res = await api.brain.items({
        params: { tenantId, projectId },
        query: { limit: 100, offset: 0 },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/**
 * What the project's Brain holds, as the admin summary counts it: every file,
 * hidden ones included. The documents tab reads its "Not organised yet" count
 * (2026-09-26, RET-12), so the backoffice says what the Brain page says.
 */
export function useBrainSummary(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: [...brainKeys.items(tenantId, projectId), "summary"],
    queryFn: async () => {
      const res = await api.brain.summary({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useCreateBrainNote(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: KnowledgeNoteInput) => {
      const res = await api.brain.createEntry({ params: { tenantId, projectId }, body: input });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: brainKeys.items(tenantId, projectId) }),
  });
}

export function useDeleteBrainNote(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) => {
      const res = await api.brain.deleteEntry({ params: { tenantId, projectId, entryId } });
      // 409 is the ingest path saying "delete the document instead".
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: brainKeys.items(tenantId, projectId) }),
  });
}
